/**
 * Admin changesets API layer — PIM Wave P5 Task 7. Typed calls to the
 * SHIPPED PIM Wave P5 Tasks 1-6 `/admin/catalog/changesets/*` router
 * (`apps/api/garden/features/catalog_schema/changesets/router.py`), gated
 * server-side on `marketplace.attribute.manage` AND the `pim.changesets`
 * feature flag (flag OFF -> every route 404s `pim.changeset.disabled`,
 * meaning "changesets are off, use P1 direct-write" — callers treat that
 * code as a graceful empty/disabled state, never a crash).
 *
 * Mirrors the Pydantic schemas in `catalog_schema/changesets/schemas.py`
 * EXACTLY: `Changeset{Summary,Response,ListResponse}`, `OpIn`/`OpView`. The
 * impact-snapshot shape mirrors `changesets/impact.py`'s documented stable
 * output (module docstring: "the FE drawer depends on every key").
 *
 * This is the DEDICATED changeset authoring surface (owner ruling
 * 2026-07-06 "Option A") — a SEPARATE surface from the P1 direct-write CRUD
 * (`attribute-fields/`, `categories/`, `category-bindings/`,
 * `need-mappings/`), which stays untouched. `ChangesetOpType` below is the
 * MVP op-builder's own subset (5 op types), not the full 27-entry
 * `ops.OP_TYPES` registry server-side — see `components/OpBuilderForm.tsx`
 * header for the "why only these five" rationale.
 */
import { apiClient, ApiError } from "@/lib/api-client";

// ---------------------------------------------------------------------
// Status + entity/action vocabularies (mirrors `changesets/models.py` /
// `changesets/ops.py` — NOT re-exported from a shared package since the
// backend has no shared-types codegen yet, per repo convention).
// ---------------------------------------------------------------------

/** `CatalogChangeset.status` values (`changesets/models.py`). */
export const CHANGESET_STATUS_VALUES = [
  "draft",
  "published",
  "rolled_back",
] as const;
export type ChangesetStatusValue = (typeof CHANGESET_STATUS_VALUES)[number];

/** `entity_type` values `ops.describe_op` ever emits (`op_type.partition(".")`
 * left-hand side across the 27-entry `ops.OP_TYPES` registry). */
export type ChangesetEntityType =
  | "group"
  | "field"
  | "option"
  | "binding"
  | "category"
  | "alias"
  | "need_mapping";

/** `action` values `ops.describe_op` ever emits (the right-hand side of
 * `op_type.partition(".")`, plus `"reorder"` for `binding.reorder`). */
export type ChangesetOpAction =
  | "create"
  | "update"
  | "archive"
  | "unarchive"
  | "delete"
  | "restore"
  | "reorder";

// ---------------------------------------------------------------------
// Core DTOs — mirrors `schemas.py` field-for-field.
// ---------------------------------------------------------------------

/** Body for `POST /admin/catalog/changesets` (mirrors `ChangesetCreate`). */
export interface ChangesetCreateBody {
  title: string;
}

/** Body for `POST .../operations` (mirrors `OpIn`). `op_type` is one of the
 * server's full `ops.OP_TYPES` registry — this client only ever SENDS the
 * MVP op-builder's five (see `ChangesetOpType` in `components/OpBuilderForm.tsx`),
 * but keeps `op_type: string` here so the type doesn't drift from the
 * server's broader acceptance. */
export interface OpInBody {
  op_type: string;
  payload: Record<string, unknown>;
  ref?: string | null;
}

/** Mirrors `OpView` — a stored op plus its data-only describe fields (the FE
 * localizes `{action} {entity_type}: {target}` itself, never rendering
 * `target` or the raw payload as trusted markup). */
export interface OpView {
  op_type: string;
  payload: Record<string, unknown>;
  ref: string | null;
  applied: Record<string, unknown> | null;
  entity_type: string;
  action: string;
  target: string;
}

/** Mirrors `ChangesetSummary` — the list-row shape. */
export interface ChangesetSummary {
  id: string;
  status: string;
  title: string;
  op_count: number;
  created_by: string;
  created_at: string;
  published_at: string | null;
  rolled_back_at: string | null;
}

/** Mirrors `ChangesetListResponse`. */
export interface ChangesetListResponse {
  changesets: ChangesetSummary[];
}

/** Mirrors `ChangesetResponse` — the full drawer-detail shape. */
export interface ChangesetResponse {
  id: string;
  status: string;
  title: string;
  operations: OpView[];
  op_count: number;
  impact_snapshot: ImpactSnapshot | null;
  touched_entities: string[] | null;
  created_by: string;
  published_by: string | null;
  published_at: string | null;
  rolled_back_by: string | null;
  rolled_back_at: string | null;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------
// Impact snapshot — mirrors `changesets/impact.py`'s documented stable
// output shape verbatim (module docstring: "Output shape (stable — the FE
// drawer depends on every key)").
// ---------------------------------------------------------------------

/** One entry of `impact_snapshot.operations` — a lighter echo of `OpView`
 * (no `payload`/`ref`/`applied`) used only for the preview panel's op recap. */
export interface ImpactOpSummary {
  op_type: string;
  entity_type: string;
  action: string;
  target: string;
}

/** One `impact_snapshot.compliance_loss` row — a `binding.create`/
 * `binding.update` that makes a field REQUIRED, with the count of currently-
 * gated products in that category's subtree that have no value for it. */
export interface ComplianceLossRow {
  category_id: string;
  category_name_ru: string;
  field_slug: string;
  product_count: number;
}

/** One `requirement_changed` entry inside a `form_diffs` row. */
export interface RequirementChangeRow {
  field_slug: string;
  to: string;
}

/** One `impact_snapshot.form_diffs` row — one per category touched by a
 * binding op in this draft. */
export interface FormDiffRow {
  category_id: string;
  category_name_ru: string;
  added: string[];
  removed: string[];
  requirement_changed: RequirementChangeRow[];
}

/** One `impact_snapshot.option_usage` row — per `option.archive` op. */
export interface OptionUsageRow {
  field_slug: string;
  option_slug: string;
  product_count: number;
}

/** Mirrors `impact.compute_impact`'s return dict exactly. */
export interface ImpactSnapshot {
  op_count: number;
  operations: ImpactOpSummary[];
  compliance_loss: ComplianceLossRow[];
  form_diffs: FormDiffRow[];
  option_usage: OptionUsageRow[];
  warnings: string[];
}

// ---------------------------------------------------------------------
// API fetchers
// ---------------------------------------------------------------------

export interface ListChangesetsParams {
  status?: ChangesetStatusValue | null;
}

export function listChangesets(
  params: ListChangesetsParams = {},
): Promise<ChangesetListResponse> {
  const qs = params.status ? `?status=${params.status}` : "";
  return apiClient.get<ChangesetListResponse>(
    `/admin/catalog/changesets${qs}`,
  );
}

export function createChangeset(
  body: ChangesetCreateBody,
): Promise<ChangesetResponse> {
  return apiClient.post<ChangesetResponse, ChangesetCreateBody>(
    "/admin/catalog/changesets",
    body,
  );
}

export function getChangeset(id: string): Promise<ChangesetResponse> {
  return apiClient.get<ChangesetResponse>(`/admin/catalog/changesets/${id}`);
}

export function appendChangesetOperation(
  id: string,
  body: OpInBody,
): Promise<ChangesetResponse> {
  return apiClient.post<ChangesetResponse, OpInBody>(
    `/admin/catalog/changesets/${id}/operations`,
    body,
  );
}

export function removeChangesetOperation(
  id: string,
  index: number,
): Promise<ChangesetResponse> {
  return apiClient.delete<ChangesetResponse>(
    `/admin/catalog/changesets/${id}/operations/${index}`,
  );
}

export function deleteChangeset(id: string): Promise<void> {
  // 204 hard delete of a draft — no response body.
  return apiClient.delete<void>(`/admin/catalog/changesets/${id}`);
}

export function previewChangeset(id: string): Promise<ChangesetResponse> {
  // Dedicated POST route (NOT a GET) — no request body; fills impact_snapshot.
  return apiClient.post<ChangesetResponse, Record<string, never>>(
    `/admin/catalog/changesets/${id}/preview`,
    {},
  );
}

export function publishChangeset(id: string): Promise<ChangesetResponse> {
  return apiClient.post<ChangesetResponse, Record<string, never>>(
    `/admin/catalog/changesets/${id}/publish`,
    {},
  );
}

export function rollbackChangeset(id: string): Promise<ChangesetResponse> {
  return apiClient.post<ChangesetResponse, Record<string, never>>(
    `/admin/catalog/changesets/${id}/rollback`,
    {},
  );
}

// ---------------------------------------------------------------------
// Query-key factory (consumed by hooks.ts).
// ---------------------------------------------------------------------

export const changesetKeys = {
  /** `["admin","changesets","list",status]` — one cache entry per status
   * filter (mirrors `attributeFieldKeys.list`'s filters-keyed shape). */
  list: (status: ChangesetStatusValue | null) =>
    ["admin", "changesets", "list", status] as const,
  /** `["admin","changesets","detail",id]`. */
  detail: (id: string) => ["admin", "changesets", "detail", id] as const,
} as const;

// ---------------------------------------------------------------------
// Error parsing helpers — mirrors `seller-dashboard/api.ts`'s
// `parsePimError` (this repo's precedent for reading a structured
// `fields[]` array off `ApiError.problem`), specialised for the two
// changeset-specific error shapes the brief calls out.
// ---------------------------------------------------------------------

/** `changesets.disabled`" gate: the `pim.changesets` feature flag is OFF.
 * Every route 404s with this code — callers render a friendly "disabled"
 * state instead of the generic error state. */
export const CHANGESETS_DISABLED_CODE = "pim.changeset.disabled";

export function isChangesetsDisabledError(error: unknown): boolean {
  return error instanceof ApiError && error.code === CHANGESETS_DISABLED_CODE;
}

/** One `fields[]` entry from a 422 `pim.changeset_trilingual_incomplete`
 * (`ChangesetTrilingualIncompleteError`, `changesets/service.py`) —
 * `field_slug` is `"<entity_type>:<uuid>"` on the wire; this helper splits
 * it into `entity_type`/`entity_id` for the drawer's per-row rendering. */
export interface TrilingualFieldError {
  entity_type: string;
  entity_id: string;
  code: string;
  message: string | null;
}

/** Parses the 422 `pim.changeset_trilingual_incomplete` publish error into
 * its `fields[]` array, or returns `null` if `error` isn't that shape (the
 * caller then falls back to a generic toast). */
export function parseChangesetTrilingualError(
  error: unknown,
): TrilingualFieldError[] | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code !== "pim.changeset_trilingual_incomplete") return null;

  const rawFields = (error.problem as Record<string, unknown>)["fields"];
  const fields: TrilingualFieldError[] = [];
  if (Array.isArray(rawFields)) {
    for (const item of rawFields) {
      if (item !== null && typeof item === "object") {
        const rec = item as Record<string, unknown>;
        const fieldSlug = rec["field_slug"];
        const code = rec["code"];
        if (typeof fieldSlug === "string" && typeof code === "string") {
          const [entityType, entityId] = fieldSlug.split(":");
          const message = typeof rec["message"] === "string" ? rec["message"] : null;
          fields.push({
            entity_type: entityType ?? "",
            entity_id: entityId ?? "",
            code,
            message,
          });
        }
      }
    }
  }
  return fields;
}

/** 409 `pim.changeset.rollback_conflict` (`ChangesetRollbackConflictError`)
 * — a later published changeset also touched a shared entity; roll IT back
 * first (linear history per entity). Returns the server's `detail` string
 * (the only structured content the base `GardenError` envelope carries for
 * this code) or `null` if `error` isn't that shape. */
export function parseChangesetRollbackConflict(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code !== "pim.changeset.rollback_conflict") return null;
  return error.problem.detail ?? null;
}
